const AbstractItem = require('@/lib/Items/AbstractItem');
const FieldEntryRequirement = require('@/lib/Forms/FieldEntryRequirement');
const GiftVoucherDenomination = require('@/lib/GiftVouchers/GiftVoucherDenomination');
const GiftVoucherTypeEnum = require('@/lib/GiftVouchers/GiftVoucherTypeEnum');
const ItemType = require('@/lib/Items/ItemType');
const { populate } = require('@/functions/objects');
const { toBool, toInt, toString } = require('@/functions/transform');

class GiftVoucher extends AbstractItem {
    constructor(data = {}) {
        super();

        this.itemType = ItemType.GIFT_VOUCHER;

        /**
         * @type {string[]}
         */
        this.availableDeliveryTypes = [];

        /**
         * @type {string}
         */
        this.collectRecipientMessage = FieldEntryRequirement.NOT_ALLOWED;

        /**
         * @type {string}
         */
        this.collectRecipientName = FieldEntryRequirement.NOT_ALLOWED;

        /**
         * @type {GiftVoucherDenomination[]}
         */
        this.denominations = [];

        /**
         * @type {?number}
         */
        this.expiresAfter = null;

        /**
         * @type {?string}
         */
        this.expiresAfterUnit = null;

        /**
         * @type {String}
         */
        this.giftVoucherType = null;

        /**
         * @type {String}
         */
        this.taxClassificationID = null;

        populate(this, data);
    }

    /**
     * @return {string}
     */
    getExpiresAfterString() {
        return this.expiresAfter
            + ' '
            + (
                this.expiresAfter === 1
                    // Remove trailing 's'
                    ? this.expiresAfterUnit.substr(0, this.expiresAfterUnit.length - 1)
                    : this.expiresAfterUnit
            );
    }

    /**
     * @return {boolean}
     */
    isBalanceType() {
        return this.giftVoucherType === GiftVoucherTypeEnum.BALANCE;
    }

    /**
     * @return {boolean}
     */
    isExperienceType() {
        return this.giftVoucherType === GiftVoucherTypeEnum.EXPERIENCE;
    }

    /**
     * Returns a GiftVoucherDenomination that may exist on this voucher that matches the value given.
     *
     * @param {number} value
     *
     * @return {GiftVoucherDenomination[]}
     */
    getDenominationsByValue(value) {
        return this.denominations.filter((d) => d.denomination === value);
    }

    /**
     * @return {boolean}
     */
    taxedOnSale() {
        // 27 is the number hardcoded on the BO for 'Outside the scope'.
        return this.taxClassificationID && this.taxClassificationID !== 27;
    }

    /**
     * @return {boolean}
     */
    canCollectRecipientMessage() {
        return this.collectRecipientMessage !== FieldEntryRequirement.NOT_ALLOWED;
    }

    /**
     * @return {boolean}
     */
    mustCollectRecipientMessage() {
        return this.collectRecipientMessage === FieldEntryRequirement.REQUIRED;
    }

    /**
     * @return {boolean}
     */
    canCollectRecipientName() {
        return this.collectRecipientName !== FieldEntryRequirement.NOT_ALLOWED;
    }

    /**
     * @return {boolean}
     */
    mustCollectRecipientName() {
        return this.collectRecipientName === FieldEntryRequirement.REQUIRED;
    }

    /**
     * @return {object}
     */
    getHydrationMap() {
        return Object.assign(
            super.getHydrationMap(),
            {
                // Unlike other item types we don't inherit allowPriceEdit from the category for gift vouchers.
                // The allowPriceEdit field specifies if a custom balance can be entered for this voucher.
                allowPriceEdit: toBool,
                availableDeliveryTypes: {
                    transform(value) {
                        return typeof value === 'string' ? value.split(',') : value;
                    },
                },
                collectRecipientMessage: toString,
                collectRecipientName: toString,
                denominations: {
                    modelCollection: GiftVoucherDenomination,
                },
                expiresAfter: toInt,
                expiresAfterUnit: toString,
                giftVoucherType: toString,
                taxClassificationID: toInt,
            }
        );
    }
}

module.exports = GiftVoucher;
